import '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('change', this.#handleVariantInputChange);
    this.addEventListener('pointerleave', this.#handleCardPointerLeave);
    this.addEventListener('pointerover', this.#handleSwatchPointerOver);
    this.addEventListener('pointerout', this.#handleSwatchPointerOut);
    this.addEventListener('pointerout', this.#handleDelegatedGalleryPointerOut);
    this.refs.cardGallery?.addEventListener('pointerleave', this.#handleGalleryPointerLeave);
    this.refs.cardGallery?.addEventListener('pointercancel', this.#handleGalleryPointerLeave);
    this.refs.cardGallery?.addEventListener('pointerdown', this.#handleGalleryInteractionStart);
    this.refs.cardGallery?.addEventListener('mouseleave', this.#handleGalleryMouseLeave);

    // Variant gallery URLs are rendered into the swatches by Liquid. Warm the
    // browser cache immediately so the first card hover is as fast as every
    // later hover and never needs to fetch/parse the product page.
    this.#preloadVariantCardImages();

    requestAnimationFrame(() => {
      const selectedInput = this.#getSelectedVariantInput();
      const hasVariantGallery = this.#activateVariantGallery(selectedInput);
      if (!hasVariantGallery && selectedInput?.dataset.primaryImageUrl) {
        const activeImage = this.#getActiveCardImage();
        const primaryImageUrl = selectedInput.dataset.primaryImageUrl;

        // The initial Liquid-rendered slide normally is this exact image.
        // Avoid placing a duplicate preview layer above it: removing that
        // layer on the first touch could cancel iOS Safari's first swipe.
        if (!this.#cardImageMatchesUrl(activeImage, primaryImageUrl)) {
          this.#displayVariantCardImage(primaryImageUrl);
        }
      }
    });

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.variantHoverHideTimer);
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('change', this.#handleVariantInputChange);
    this.removeEventListener('pointerleave', this.#handleCardPointerLeave);
    this.removeEventListener('pointerover', this.#handleSwatchPointerOver);
    this.removeEventListener('pointerout', this.#handleSwatchPointerOut);
    this.removeEventListener('pointerout', this.#handleDelegatedGalleryPointerOut);
    this.refs.cardGallery?.removeEventListener('pointerleave', this.#handleGalleryPointerLeave);
    this.refs.cardGallery?.removeEventListener('pointercancel', this.#handleGalleryPointerLeave);
    this.refs.cardGallery?.removeEventListener('pointerdown', this.#handleGalleryInteractionStart);
    this.refs.cardGallery?.removeEventListener('mouseleave', this.#handleGalleryMouseLeave);
  }

  #handleSwatchPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const label = event.target.closest('label[data-media-id]');
    if (!(label instanceof HTMLElement) || !this.contains(label)) return;
    if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

    const input = label.querySelector('input[data-variant-id]');
    if (!(input instanceof HTMLInputElement)) return;

    this.hoveredVariantInput = input;
    // A shopper normally pauses on the swatch before reaching the gallery.
    // Use that interval to fetch both gallery images at high priority so the
    // first image hover is as immediate as subsequent hovers.
    this.#warmVariantInput(input, 'high');
    if (this.#usesCollectionImageCrossfade() && this.#getVariantPrimaryImageUrl(input)) return;
    if (this.#activateVariantGallery(input)) return;
    const primaryUrl = input.dataset.primaryImageUrl;
    if (primaryUrl) {
      this.#displayVariantCardImage(primaryUrl);
      return;
    }

    const mediaId = label.dataset.mediaId;
    if (mediaId) this.previewVariant(mediaId);
  };

  #handleSwatchPointerOut = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const label = event.target.closest('label[data-media-id]');
    if (!(label instanceof HTMLElement) || !this.contains(label)) return;
    if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

    const relatedTarget = event.relatedTarget;
    const gallery = this.refs.cardGallery;

    // Preserve this swatch only while the pointer travels directly into the
    // media gallery. The gallery hover should then use this same variant's
    // second metafield image. Moving anywhere else in the card restores the
    // selected variant immediately so stale swatch state cannot strand image
    // two on products with larger cards or overlapping quick-add controls.
    if (relatedTarget instanceof Node && gallery?.contains(relatedTarget)) return;

    const relatedLabel =
      relatedTarget instanceof Element
        ? relatedTarget.closest('label[data-media-id]')
        : null;
    const relatedInput = relatedLabel?.querySelector('input[data-variant-id]');

    if (relatedInput instanceof HTMLInputElement && this.contains(relatedInput)) {
      this.hoveredVariantInput = relatedInput;
      this.#warmVariantInput(relatedInput, 'high');
      if (this.#usesCollectionImageCrossfade() && this.#getVariantPrimaryImageUrl(relatedInput)) return;
      if (this.#activateVariantGallery(relatedInput)) return;
      const primaryUrl = relatedInput.dataset.primaryImageUrl;
      if (primaryUrl) this.#displayVariantCardImage(primaryUrl);
      return;
    }

    this.resetVariant.cancel();
    this.hoveredVariantInput = null;
    this.#resetVariant();
  };

  #handleVariantInputChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input[data-variant-id]')) return;

    this.#galleryInteractedSinceVariantChange = false;
    this.hoveredVariantInput = null;
    this.#warmVariantInput(input, 'high');
    if (this.#usesCollectionImageCrossfade() && this.#getVariantPrimaryImageUrl(input)) {
      void this.#transitionToVariantImage(input, 'selected');
      return;
    }
    if (this.#activateVariantGallery(input)) return;
    const mediaId = input.dataset.optionMediaId;
    if (mediaId) this.refs.slideshow?.select({ id: mediaId }, undefined, { animate: false });
    const primaryUrl = input.dataset.primaryImageUrl;
    if (primaryUrl) this.#displayVariantCardImage(primaryUrl);
  };

  #handleCardPointerLeave = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.resetVariant.cancel();
    this.hoveredVariantInput = null;
    this.#resetVariant();
  };

  #handleGalleryPointerLeave = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    // If the pointer is returning directly to a swatch, restore that swatch's
    // first image. Otherwise clear the temporary swatch state and restore the
    // selected variant. This prevents the secondary hover image from becoming
    // stranded when an overlay or a gallery DOM update swallows the
    // declarative pointerleave callback.
    const relatedTarget = event.relatedTarget;
    const relatedLabel =
      relatedTarget instanceof Element
        ? relatedTarget.closest('label[data-media-id]')
        : null;
    const relatedInput = relatedLabel?.querySelector('input[data-variant-id]');

    if (relatedInput instanceof HTMLInputElement && this.contains(relatedInput)) {
      this.hoveredVariantInput = relatedInput;
      this.#warmVariantInput(relatedInput, 'high');
      if (this.#usesCollectionImageCrossfade() && this.#getVariantPrimaryImageUrl(relatedInput)) return;
      if (this.#activateVariantGallery(relatedInput)) return;
      const primaryUrl = relatedInput.dataset.primaryImageUrl;
      if (primaryUrl && this.#displayVariantCardImage(primaryUrl)) return;
    } else {
      this.hoveredVariantInput = null;
    }

    this.#resetVariant();
  };

  #handleGalleryMouseLeave = (event) => {
    // Pointer events are the primary path. This mouse fallback covers browser
    // edge cases where a native control, scrollbar, or DOM replacement causes
    // pointerleave to be skipped while mouseleave still fires.
    this.#handleGalleryPointerLeave(event);
  };

  #handleGalleryInteractionStart = (event) => {
    if (event.target instanceof Element && event.target.closest('button, [role="button"]')) return;

    // A variant update may still be resolving while the shopper begins a
    // swipe. Preserve this interaction so the late response cannot reset the
    // gallery back to its first slide.
    this.#galleryInteractedSinceVariantChange = true;

    // Horizon enables slideshow overflow through an asynchronous viewport
    // observer. Make the carousel swipe-ready before this gesture continues,
    // even when that observer has not fired yet.
    if (this.refs.slideshow instanceof HTMLElement) {
      this.refs.slideshow.setAttribute('in-viewport', '');
    }

    this.#hideVariantPreviewImage();
  };

  #handleDelegatedGalleryPointerOut = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const gallery = event.target.closest('[ref="cardGallery"], .card-gallery');
    if (!(gallery instanceof HTMLElement) || !this.contains(gallery)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && gallery.contains(relatedTarget)) return;

    this.#handleGalleryPointerLeave(event);
  };

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html);
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        if (!this.#galleryInteractedSinceVariantChange) this.#updateVariantImages();
        this.#previousSlideIndex = null;

        // Horizon's variant response can update the slideshow after the input
        // change handler has already shown the metafield image. Re-assert the
        // selected variant's first gallery image after that update settles.
        requestAnimationFrame(() => {
          if (this.#galleryInteractedSinceVariantChange) return;
          const selectedInput = this.#getSelectedVariantInput();
          if (this.#activateVariantGallery(selectedInput)) return;
          const primaryUrl = selectedInput?.dataset.primaryImageUrl;
          if (primaryUrl) this.#displayVariantCardImage(primaryUrl);
        });

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #updateProductUrl(html) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    if (this.#activateVariantGallery(this.#getSelectedVariantInput())) return;

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  #getSelectedVariantInput() {
    const checkedInput = this.querySelector('input[data-variant-id]:checked');
    if (checkedInput instanceof HTMLInputElement) return checkedInput;

    const selectedOption = this.variantPicker?.selectedOption;
    if (selectedOption instanceof HTMLInputElement) return selectedOption;

    const firstInput = this.querySelector('input[data-variant-id]');
    return firstInput instanceof HTMLInputElement ? firstInput : null;
  }

  /**
   * Reveals the selected variant's custom metafield gallery. All galleries are
   * rendered with the card, so switching a swatch and the first swipe require
   * no fetch or DOM rebuild. Product media is used only when the variant does
   * not have a custom gallery.
   * @param {HTMLInputElement | null | undefined} input
   * @returns {boolean} Whether a variant metafield gallery was activated.
   */
  #activateVariantGallery(input) {
    const slideshow = this.refs.slideshow;
    if (!(slideshow instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return false;

    this.#hideVariantHoverImage();

    const slots = [...slideshow.querySelectorAll('slideshow-slide[data-card-gallery-slot]')];
    if (!slots.length) return false;

    let galleryUrls = [];
    try {
      const parsed = JSON.parse(input.dataset.variantGallery || '[]');
      if (Array.isArray(parsed)) galleryUrls = parsed.filter(url => typeof url === 'string' && url);
    } catch {
      galleryUrls = [];
    }

    const fallbackUrls = slots
      .map(slide => slide.getAttribute('data-card-gallery-fallback-url'))
      .filter(Boolean);
    const activeUrls = galleryUrls.length ? galleryUrls : fallbackUrls;
    if (!activeUrls.length) return false;

    for (const [index, slide] of slots.entries()) {
      const url = activeUrls[index];
      slide.hidden = !url;
      slide.removeAttribute('reveal');
      if (!url) continue;

      const image = slide.querySelector('img');
      if (!(image instanceof HTMLImageElement)) continue;

      const resolvedUrl = new URL(url, window.location.origin).href;
      if (!this.#cardImageMatchesUrl(image, resolvedUrl)) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = resolvedUrl;
      }
    }

    const firstSlide = slots[0];
    const slideId = firstSlide.getAttribute('slide-id');
    if (slideId && typeof slideshow.select === 'function') {
      slideshow.select({ id: slideId }, undefined, { animate: false });
    }

    this.#previousSlideIndex = null;
    this.#hideVariantPreviewImage();
    return galleryUrls.length > 0;
  }

  #getActiveCardImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const currentImage = currentSlide?.querySelector('img');
    if (currentImage instanceof HTMLImageElement) return currentImage;

    const visibleImage = this.refs.cardGallery?.querySelector(
      'slideshow-slide[aria-hidden="false"] img, slideshow-slide:not([hidden]) img, img'
    );
    return visibleImage instanceof HTMLImageElement ? visibleImage : null;
  }

  #cardImageMatchesUrl(image, url) {
    if (!(image instanceof HTMLImageElement) || !url) return false;

    try {
      const activeUrl = new URL(image.currentSrc || image.src, window.location.origin);
      const candidateUrl = new URL(url, window.location.origin);
      return activeUrl.pathname === candidateUrl.pathname;
    } catch {
      return false;
    }
  }

  #usesCollectionImageCrossfade() {
    return this.closest('results-list[id^="ZcCollectionGrid-"]') !== null;
  }

  #getVariantPrimaryImageUrl(input) {
    if (!(input instanceof HTMLInputElement)) return '';
    if (input.dataset.primaryImageUrl) return input.dataset.primaryImageUrl;

    try {
      const galleryUrls = JSON.parse(input.dataset.variantGallery || '[]');
      return Array.isArray(galleryUrls) && typeof galleryUrls[0] === 'string' ? galleryUrls[0] : '';
    } catch {
      return '';
    }
  }

  async #transitionToVariantImage(input, intent = 'hover') {
    if (!(input instanceof HTMLInputElement)) return false;

    const primaryUrl = this.#getVariantPrimaryImageUrl(input);
    if (!primaryUrl) return false;

    const requestId = (this.variantSwitchRequestId || 0) + 1;
    this.variantSwitchRequestId = requestId;
    this.#warmVariantInput(input, 'high');

    const didFade = await this.#displayVariantHoverImage(primaryUrl, true);
    if (!didFade || this.variantSwitchRequestId !== requestId) return true;
    if (intent === 'hover' && this.hoveredVariantInput !== input) return true;
    if (intent !== 'hover' && !input.checked) return true;

    // Commit the selected variant beneath the now-opaque transition layer.
    // The layer then fades away over an identical image, so the gallery state
    // changes without a visible source flash and remains swipe-ready.
    if (this.#activateVariantGallery(input)) return true;
    return this.#displayVariantCardImage(primaryUrl);
  }

  /**
   * Returns the dedicated image layer used for temporary/selected variant
   * previews. Keeping this separate from Horizon's slideshow prevents its
   * responsive image updates from overwriting a reset (the cause of cards
   * occasionally remaining on image 2).
   * @returns {HTMLImageElement | null}
   */
  #getVariantPreviewImage() {
    const gallery = this.refs.cardGallery;
    if (!(gallery instanceof HTMLElement)) return null;

    let preview = gallery.querySelector('img[data-card-variant-preview]');
    if (preview instanceof HTMLImageElement) return preview;

    preview = document.createElement('img');
    preview.dataset.cardVariantPreview = 'true';
    preview.className = 'card-gallery__variant-preview';
    preview.alt = '';
    preview.decoding = 'async';
    preview.draggable = false;
    preview.hidden = true;

    // Inline the essential layout rules so this behavior does not depend on
    // a separate card-gallery stylesheet being uploaded at the same time.
    Object.assign(preview.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '1',
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      objectPosition: 'center',
      pointerEvents: 'none',
    });

    const mediaSurface = gallery.querySelector('.product-media-container');
    if (mediaSurface instanceof HTMLElement) {
      preview.style.backgroundColor = getComputedStyle(mediaSurface).backgroundColor;
    }

    gallery.prepend(preview);
    return preview;
  }

  #displayVariantCardImage(url) {
    if (!url) return false;

    this.#hideVariantHoverImage();

    const image = this.#getVariantPreviewImage();
    if (!image) return false;

    const resolvedUrl = new URL(url, window.location.origin).href;
    image.dataset.pendingUrl = resolvedUrl;

    if (image.src !== resolvedUrl) image.src = resolvedUrl;
    image.hidden = false;
    image.style.display = 'block';
    return true;
  }

  #getVariantHoverImageLayer() {
    const gallery = this.refs.cardGallery;
    if (!(gallery instanceof HTMLElement)) return null;

    let preview = gallery.querySelector('img[data-card-hover-preview]');
    if (preview instanceof HTMLImageElement) return preview;

    preview = document.createElement('img');
    preview.dataset.cardHoverPreview = 'true';
    preview.className = 'card-gallery__hover-preview';
    preview.alt = '';
    preview.decoding = 'async';
    preview.draggable = false;
    preview.hidden = true;

    Object.assign(preview.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '2',
      display: 'none',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      objectPosition: 'center',
      opacity: '0',
      pointerEvents: 'none',
      transition:
        'opacity var(--zc-image-crossfade-duration, 460ms) cubic-bezier(0.16, 1, 0.3, 1), transform var(--zc-image-crossfade-duration, 460ms) cubic-bezier(0.16, 1, 0.3, 1)',
    });

    const mediaSurface = gallery.querySelector('.product-media-container');
    if (mediaSurface instanceof HTMLElement) {
      preview.style.backgroundColor = getComputedStyle(mediaSurface).backgroundColor;
    }

    gallery.prepend(preview);
    return preview;
  }

  async #displayVariantHoverImage(url, waitForTransition = false) {
    if (!url) return false;

    const image = this.#getVariantHoverImageLayer();
    if (!image) return false;

    clearTimeout(this.variantHoverHideTimer);
    const resolvedUrl = new URL(url, window.location.origin).href;
    const requestId = String((Number(image.dataset.requestId) || 0) + 1);
    image.dataset.requestId = requestId;
    image.dataset.pendingUrl = resolvedUrl;
    image.hidden = false;
    image.style.display = 'block';
    image.style.opacity = '0';

    if (!this.#cardImageMatchesUrl(image, resolvedUrl)) image.src = resolvedUrl;

    try {
      await image.decode();
    } catch {
      // A decoded image is preferred, but the load event can still complete it.
    }

    if (image.dataset.requestId !== requestId || image.dataset.pendingUrl !== resolvedUrl) return false;

    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (image.dataset.requestId !== requestId || image.dataset.pendingUrl !== resolvedUrl) return false;

    image.style.opacity = '1';
    if (!waitForTransition) return true;

    const durationValue = getComputedStyle(image).transitionDuration.split(',')[0]?.trim() || '0s';
    const duration = durationValue.endsWith('ms')
      ? Number.parseFloat(durationValue)
      : Number.parseFloat(durationValue) * 1000;

    if (Number.isFinite(duration) && duration > 0) {
      await new Promise(resolve => setTimeout(resolve, duration + 34));
    }

    return image.dataset.requestId === requestId && image.dataset.pendingUrl === resolvedUrl;
  }

  #hideVariantHoverImage() {
    const image = this.refs.cardGallery?.querySelector('img[data-card-hover-preview]');
    if (!(image instanceof HTMLImageElement)) return;

    image.dataset.requestId = String((Number(image.dataset.requestId) || 0) + 1);
    delete image.dataset.pendingUrl;
    image.style.opacity = '0';
    clearTimeout(this.variantHoverHideTimer);
    this.variantHoverHideTimer = setTimeout(() => {
      if (image.style.opacity !== '0' || image.dataset.pendingUrl) return;
      image.hidden = true;
      image.style.display = 'none';
    }, 700);
  }

  #hideVariantPreviewImage() {
    const image = this.refs.cardGallery?.querySelector('img[data-card-variant-preview]');
    if (!(image instanceof HTMLImageElement)) return;

    image.hidden = true;
    image.style.display = 'none';
    delete image.dataset.pendingUrl;
  }

  #preloadVariantCardImages() {
    const selectedInput = this.#getSelectedVariantInput();
    this.#warmVariantInput(selectedInput, 'high');
  }

  #warmVariantInput(input, priority = 'high') {
    if (!(input instanceof HTMLInputElement)) return;
    this.#preloadVariantCardImage(input.dataset.primaryImageUrl, priority);
    this.#preloadVariantCardImage(input.dataset.hoverImageUrl, priority);
  }

  #preloadVariantCardImage(url, priority = 'low') {
    if (!url) return null;

    const resolvedUrl = new URL(url, window.location.origin).href;
    this.variantCardImagePreloads ??= new Map();

    const cached = this.variantCardImagePreloads.get(resolvedUrl);
    if (cached instanceof HTMLImageElement) {
      if (priority === 'high') cached.fetchPriority = 'high';
      return cached;
    }

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = priority;
    image.src = resolvedUrl;
    this.variantCardImagePreloads.set(resolvedUrl, image);
    image.decode?.().catch(() => {});
    return image;
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {boolean} */
  #galleryInteractedSinceVariantChange = false;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#galleryInteractedSinceVariantChange = true;
      this.#previousSlideIndex = event.detail.index;
      this.#hideVariantHoverImage();
      this.#hideVariantPreviewImage();
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  async getVariantHoverImage(input) {
    if (!(input instanceof HTMLInputElement)) return '';

    // Liquid resolves the variant metafield once while rendering the card.
    // Never fetch the PDP on first hover: that caused both the visible delay
    // and the incorrect fallback to the ordinary product gallery.
    return input.dataset.hoverImageUrl || '';
  }

  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    const hoveredInput = [...this.allVariants].find(
      input => input.dataset.optionMediaId === String(id)
    );

    if (hoveredInput) {
      this.hoveredVariantInput = hoveredInput;
      if (this.#usesCollectionImageCrossfade() && this.#getVariantPrimaryImageUrl(hoveredInput)) {
        void this.#transitionToVariantImage(hoveredInput, 'hover');
        return;
      }
      if (hoveredInput.dataset.primaryImageUrl) {
        this.#displayVariantCardImage(hoveredInput.dataset.primaryImageUrl);
        return;
      }
    }
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */

  async previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const hoverInput =
      this.hoveredVariantInput ||
      this.#getSelectedVariantInput();

    this.#warmVariantInput(hoverInput, 'high');

    // Liquid has already resolved the exact source order for this swatch:
    // variant metafield gallery image 2, then overall product gallery image 2.
    // Do not infer the source again from slideshow DOM; Horizon may hide or
    // reorder variant slides, which made a valid variant hover image look like
    // a general-gallery fallback.
    const hoverImageUrl = hoverInput?.dataset.hoverImageUrl || '';

    const useCrossfade = this.closest('results-list[id^="ZcCollectionGrid-"]') !== null;

    if (hoverImageUrl) {
      if (useCrossfade && (await this.#displayVariantHoverImage(hoverImageUrl))) return;
      if (!useCrossfade && this.#displayVariantCardImage(hoverImageUrl)) return;
    }

    if (useCrossfade) {
      const fallbackHoverImage = slideshow.querySelector(
        'slideshow-slide[data-card-gallery-slot="1"]:not([hidden]) img'
      );
      const fallbackHoverUrl =
        fallbackHoverImage instanceof HTMLImageElement
          ? fallbackHoverImage.currentSrc || fallbackHoverImage.src
          : '';
      if (fallbackHoverUrl && (await this.#displayVariantHoverImage(fallbackHoverUrl))) return;
    }

    // If the active variant has no variant-specific second image, continue
    // to the normal slideshow fallback. With the intended media ordering,
    // this displays the general gallery's second image (the lifestyle image).
    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, {
        animate: false,
      });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#handleGalleryPointerLeave(event);
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.#hideVariantHoverImage();
    this.#galleryInteractedSinceVariantChange = false;
    const selectedInput = this.#getSelectedVariantInput();
    if (this.#usesCollectionImageCrossfade() && selectedInput && this.#getVariantPrimaryImageUrl(selectedInput)) {
      void this.#transitionToVariantImage(selectedInput, 'selected');
      return;
    }
    if (this.#activateVariantGallery(selectedInput)) return;
    const primaryUrl = selectedInput?.dataset.primaryImageUrl;
    if (primaryUrl && this.#displayVariantCardImage(primaryUrl)) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(() => {
    // The declarative swatch pointerleave may fire while travelling into the
    // gallery. Preserve the preview only while a swatch or the gallery itself
    // is truly hovered—not merely while the pointer is somewhere in the card.
    const swatchIsHovered = Boolean(this.querySelector('label[data-media-id]:hover'));
    const galleryIsHovered = this.refs.cardGallery?.matches(':hover') === true;
    if (swatchIsHovered || galleryIsHovered) return;

    this.hoveredVariantInput = null;
    this.#resetVariant();
  }, 40);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(StandardEvents.productSelect, this.#handleCardProductSelect.bind(this));
    this.addEventListener('click', this.#handleOverflowButtonClick);
  }

  #handleOverflowButtonClick = (event) => {
    const clickedMoreButton = event
      .composedPath()
      .find(
        (node) =>
          node instanceof Element &&
          node.matches('button.hidden-swatches__count')
      );

    if (!clickedMoreButton || !this.contains(clickedMoreButton)) return;
    this.showAllSwatches(event);
  };

  /**
   * Updates the card URL when a variant is selected.
   * @param {ProductSelectEvent} event
   */
  #handleCardProductSelect(event) {
    // Handle URL update via promise resolution
    event.promise
      .then(() => {
        if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
          const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
          currentUrl.searchParams.set('variant', this.pendingVariantId);
          this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
          this.pendingVariantId = null;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    // Do not rely only on `instanceof`: Shopify can load the same theme
    // module under more than one versioned URL during section rendering,
    // producing two valid OverflowList constructors.
    if (overflowList && typeof overflowList.showAll === 'function') {
      overflowList.showAll();
    } else if (overflowList instanceof HTMLElement) {
      // Last-resort fallback while the custom element is still upgrading.
      overflowList.setAttribute('disabled', 'true');
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
